// client/src/hooks/useCardOrders.js
// TanStack Query hooks for the card-order resource. The create
// mutation accepts a FormData (multipart) so the logo can ride
// along — `api.post` only handles JSON, so we hand-roll fetch here.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';

const listKey            = (campId) => ['camps', campId, 'card-orders'];
const unprintedCountKey  = (campId) => ['camps', campId, 'card-orders', 'unprinted-count'];

export function useCardOrders(campId) {
  return useQuery({
    queryKey: listKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}/card-orders`),
    enabled: !!campId,
  });
}

export function useUnprintedStudentCount(campId) {
  return useQuery({
    queryKey: unprintedCountKey(campId),
    queryFn: () => api.get(`/api/camps/${campId}/card-orders/unprinted-count`),
    enabled: !!campId,
  });
}

export function useCreateCardOrder(campId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData) => {
      // formData is a FormData instance built by the page (string fields
      // + optional `logo` File). credentials:same-origin keeps the
      // tictac_session cookie attached. Don't set Content-Type — the
      // browser fills it in with the right multipart boundary.
      const res = await fetch(`/api/camps/${campId}/card-orders`, {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
      });
      const text = await res.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { /* leave null */ }
      if (!res.ok) {
        throw new ApiError({
          status:  res.status,
          code:    payload?.error,
          message: payload?.message,
          details: payload?.details,
        });
      }
      return payload;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['camps', campId, 'card-orders'] });
    },
  });
}
